// @ts-strict-ignore
import { Component, Input, OnInit } from '@angular/core';
import { ArticleUsageVO, ArticleVO } from '@article/model/vo/article.vo';
import { ArticleUsageUtils } from '@article/util/article-usage-utils';
import { ArticleUtils } from '@article/util/article-utils';
import { ArticlePlausiPropertyDef } from '@article/constants/article-plausi-property-def';
import { ValidatorFactory } from '@shared/validator.factory';
import { UserUtils } from '@user/service/user-utils';
import { UserModel } from '@user/model/user.model';
import { FormBuilder, FormControl, FormGroup, Validators } from '@angular/forms';
import { BsModalRef } from 'ngx-bootstrap/modal';
import { IOConsumptionResultVO } from '@project/model/vo/validation-context.vo';
import { MetaElementPlausiPropertyDef } from '@project/constants/meta-element-plausi-property-def';
import { MetaElementVO } from '@shared/vo/meta-element.vo';
import { NumberUtils } from '@shared/util/number-utils';
import { PropertyValueType } from '@project/constants/property-value-type';
import { PropertyUtils } from '@article/util/property-utils';
import { MetaElementBehaviorPropertyDef } from '@project/constants/meta-element-behavior-property-def';
import { StringUtils } from '@shared/util/string-utils';
import { ArticleUsagePlausiPropertyDef } from '@article/constants/article-usage-plausi-property-def';
import { DefinedMetaElementPlausiPropertyDef } from '@project/components/project/constants/defined-meta-element-plausi-property-def';
import { PropertyValueVO } from '../model/vo/property.vo';
import { ProjectModel } from '@project/model/project.model';
import { ValidationContextDefs } from '@project/components/project/constants/validation-context-def';
import { ValidationService } from '@project/manager/validation.service';
import { OverlayService } from '@shared/overlay/overlay.service';
import { OverlayBackground } from '@shared/overlay/overlay.type';
import { LocaleResourceManager } from '@shared/manager/locale-resource.manager';
import { ArticleUsageService } from '@article/service/article-usage.service';
import { ProjectComponentPresenter } from '../../project-component.presenter';

@Component({
  standalone: false,
  selector: 'io-article-settings-tab',
  templateUrl: './io-article-settings-tab.component.html',
})
export class IOArticleSettingsTabComponent implements OnInit {
  @Input() articleUsage: ArticleUsageVO;

  private settingsArticle: ArticleVO;

  protected consumptionResult: IOConsumptionResultVO;

  public editProjectPermission: boolean;
  public busKopplerModule: boolean;
  public potantialFix: boolean;
  public channeldataProvider: any[] = [];

  public form: FormGroup;

  constructor(
    private fb: FormBuilder,
    private bsModalRef: BsModalRef,
    private userModel: UserModel,
    private projectModel: ProjectModel,
    private validationService: ValidationService,
    private overlayService: OverlayService,
    private localeResourceManager: LocaleResourceManager,
    private articleUsageService: ArticleUsageService,
    private projectComponentPresenter: ProjectComponentPresenter,
    private validatorFactory: ValidatorFactory,
  ) {}

  public ngOnInit(): void {
    if (!this.articleUsage) {
      this.close();
    }

    this.editProjectPermission = UserUtils.hasEditProjectPermission(this.userModel.user);
    this.settingsArticle = this.articleUsageService.getArticleByArticleUsage(this.articleUsage);
    this.busKopplerModule = this.isBusKopplerModule();
    this.potantialFix = this.isPotantialFix();
    this.validateProject();
  }

  public close(): void {
    this.bsModalRef.hide();
  }

  public save(): void {
    if (!this.potantialFix) {
      PropertyUtils.setIntPropertyValue(
        this.articleUsage.plausiProperties,
        'potentialToUse',
        this.localeResourceManager.parseNumber(this.form.controls.potentialToUse.value)
      );
    }
    this.channeldataProvider.forEach(item => item.saveAction(this.form.controls[item.propertyLabel].value));
    this.projectModel.notifyCurrentProjectChanged();
    this.close();
  }

  private validateProject(): void {
    this.overlayService.requestOverlayShow(OverlayBackground.color);
    this.validationService.updateRepresentationOfCurrentProjectForValidationPurposes();
    this.validationService.calculateIO(this.projectModel.currentProject).subscribe(
      (consumptionResult: IOConsumptionResultVO) => {
        this.projectValidatedSuccessHandler(consumptionResult);
      },
      (error) => {
        this.projectValidatedFailureHandler(error);
      }
    );
  }

  private projectValidatedSuccessHandler(consumptionResult: IOConsumptionResultVO): void {
    this.overlayService.requestOverlayHide(OverlayBackground.color);
    if (!consumptionResult) {
      return;
    }
    this.consumptionResult = consumptionResult;
    this.updateForm();
  }

  private projectValidatedFailureHandler(message: any): void {
    this.overlayService.requestOverlayHide(OverlayBackground.color);
    if (!message || !message.validationContext) {
      return;
    }
    if (message.validationContext.id !== ValidationContextDefs.ID_WAGO_SPEEDWAY_CALCULATIONS) {
      return;
    }
  }

  private updateForm(): void {
    let usageId = this.articleUsage.id;

    if (ArticleUsageUtils.isCompositePartArticleUsage(this.articleUsage)) {
      usageId = ArticleUsageUtils.getCompositeArticleUsageIdFromUsage(this.articleUsage);
    }

    const articleConsumption = this.consumptionResult.articleConsumption.find((a) => a.articleReference === usageId);

    if (isNaN(PropertyUtils.getIntPropertyValue(this.articleUsage.plausiProperties, 'isDetailedEntryForCurrentConsumption'))) {
      PropertyUtils.setIntPropertyValue(this.articleUsage.plausiProperties, 'isDetailedEntryForCurrentConsumption', 1);
    }

    this.form = this.fb.group({});
    this.form.addControl(
      'isDetailedEntryForCurrentConsumption750_753InputItem',
      new FormControl({
        value: PropertyUtils.getIntPropertyValue(this.articleUsage.plausiProperties, 'isDetailedEntryForCurrentConsumption'),
        disabled: !this.editProjectPermission,
      })
    );

    this.form.addControl(
      'totalInternalSensorCurrentConsumption',
      new FormControl({
        value: PropertyUtils.getNumberPropertyValue(this.settingsArticle.plausiProperties, 'internalCurrentConsumption', 0),
        disabled: !this.editProjectPermission,
      })
    );

    this.form.addControl(
      'logicAndSensorCurrentCarryover',
      new FormControl({
        value: this.localeResourceManager.formatNumber(articleConsumption.remainingBusCurrent, true, 2, true),
        disabled: !this.editProjectPermission,
      })
    );

    this.form.addControl(
      'actuatorCurrentCarryover',
      new FormControl({
        value: this.localeResourceManager.formatNumber(articleConsumption.remainingFieldCurrent, true, 2, true),
        disabled: !this.editProjectPermission,
      })
    );

    this.form.addControl(
      'totalActuatorCurrentConsumption',
      new FormControl({
        value: this.localeResourceManager.formatNumber(
          articleConsumption.fieldCurrentConsumption + articleConsumption.fieldInternalCurrentConsumption,
          true,
          2,
          true
        ),
        disabled: !this.editProjectPermission,
      })
    );

    this.form.addControl(
      'totalInternalActuatorCurrentConsumption',
      new FormControl({
        value: this.localeResourceManager.formatNumber(articleConsumption.fieldInternalCurrentConsumption, true, 2, true),
        disabled: !this.editProjectPermission,
      })
    );

    this.form.addControl(
      'potentialToUse',
      new FormControl({
        value: articleConsumption.measuredVoltage ? this.localeResourceManager.formatNumber(articleConsumption.measuredVoltage, true, 0, true) : null,
        disabled: !this.editProjectPermission,
      })
    );

    this.form.addControl(
      'potentialValueLabel',
      new FormControl({
        value: this.createPotantialValueText(),
        disabled: !this.editProjectPermission,
      })
    );

    this.channeldataProvider = this.getChannelDataProvider();
    this.channeldataProvider.forEach((item) => this.form.addControl(item.propertyLabel, new FormControl(item.defaultValue, item.validator)));
  }

  public detailedEntryForCurrentConsumptionChangedHandler(): void {
    PropertyUtils.setIntPropertyValue(
      this.articleUsage.plausiProperties,
      'isDetailedEntryForCurrentConsumption',
      Number(this.form.controls.isDetailedEntryForCurrentConsumption750_753InputItem.value)
    );
    this.updateForm();
  }

  private createPotantialValueText(): string {
    if (!this.settingsArticle) {
      return '';
    }
    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValueByPropertyName(
      this.settingsArticle.plausiProperties,
      MetaElementPlausiPropertyDef.SPANNUNG_IO
    );
    if (!propertyValue) {
      return '';
    }

    if (propertyValue.type === PropertyValueType.INT) {
      return propertyValue.intValue + '';
    } else {
      return propertyValue.intRange.left + ' - ' + propertyValue.intRange.right;
    }
  }

  private isBusKopplerModule(): boolean {
    return ArticleUtils.getBooleanArticlePlausiProperty(this.settingsArticle, ArticlePlausiPropertyDef.MAX_CURRENT_FOR_BUS_MODULES);
  }

  private isPotantialFix(): boolean {
    if (!this.settingsArticle) {
      return false;
    }
    const propertyValue: PropertyValueVO = PropertyUtils.getFirstPropertyValueByPropertyName(
      this.settingsArticle.plausiProperties,
      MetaElementPlausiPropertyDef.SPANNUNG_IO
    );
    if (!propertyValue) {
      return true;
    }
    return propertyValue.type === PropertyValueType.INT;
  }

  public hasCurrentChannels(): boolean {
    let metaElements: MetaElementVO[] = ArticleUtils.getConnectionAreasByValidBooleanPlausiPropertyName(
      this.settingsArticle,
      MetaElementPlausiPropertyDef.IS_ACTUATOR_CURRENT_CHANNEL
    );
    if (metaElements.length > 0) {
      return true;
    }
    const values: PropertyValueVO[] = PropertyUtils.getPropertyValuesByPropertyName(
      this.settingsArticle.plausiProperties,
      MetaElementPlausiPropertyDef.IS_ACTUATOR_CURRENT_CHANNEL
    );
    if (values.length > 0) {
      return true;
    }
    metaElements = ArticleUtils.getConnectionAreasByValidBooleanPlausiPropertyName(
      this.settingsArticle,
      MetaElementPlausiPropertyDef.IS_LOGIC_CURRENT_CHANNEL
    );
    if (metaElements.length > 0) {
      return true;
    }
    metaElements = ArticleUtils.getConnectionAreasByValidBooleanPlausiPropertyName(
      this.settingsArticle,
      MetaElementPlausiPropertyDef.IS_SENSOR_CURRENT_CHANNEL
    );
    if (metaElements.length > 0) {
      return true;
    }
    return false;
  }

  public hasInputMode(): Boolean {
    const inputMode: number = ArticleUsageUtils.getArticleUsagePlausiPropertyIntValue(this.articleUsage, 'isDetailedEntryForCurrentConsumption');
    if (NumberUtils.isNotNumber(inputMode)) {
      return false;
    }
    return true;
  }

  private getChannelDataProvider(): any[] {
    return this.getCurrentChannelDataProvider(
      MetaElementPlausiPropertyDef.IS_ACTUATOR_CURRENT_CHANNEL,
      DefinedMetaElementPlausiPropertyDef.ACTUATOR_CURRENT_CONSUMPTION,
      ArticleUsagePlausiPropertyDef.TOTAL_ACTUATOR_CURRENT_CONSUMPTION,
      ArticleUsagePlausiPropertyDef.USED_ACTUATOR_CHANNEL_COUNT,
      Number(this.form.controls.isDetailedEntryForCurrentConsumption750_753InputItem.value)
    );
  }

  private getCurrentChannelDataProvider(
    metaElementSelectionPlausiPropertyName: string,
    inputTargetMetaPropertyName: string,
    inputTargetTotalArticlePropertyName: string,
    inputTargetChannelCountArticlePropertyName: string,
    isDetailedEntryForCurrentConsumption: number
  ): any[] {
    const dataProvider: any[] = [];
    if (!this.articleUsage) {
      return dataProvider;
    }
    const article: ArticleVO = this.articleUsageService.getArticleByArticleUsage(this.articleUsage);
    const ioChannel: any[] = this.getIOChannel(article, metaElementSelectionPlausiPropertyName);
    if (isDetailedEntryForCurrentConsumption === 1) {
      for (const channel of ioChannel) {
        const defaultValue: string = this.localeResourceManager.formatNumber(
          ArticleUsageUtils.getDefinedMetaElementPlausiPropertyFloatValue(this.articleUsage, channel.name, inputTargetMetaPropertyName),
          false
        );
        const obj: any = {
          defaultValue: defaultValue,
          propertyName: inputTargetMetaPropertyName,
          propertyLabel: channel.label,
          metaElementName: channel.name,
          inputRestricter: '^[0-9.,]*$',
          validator: Validators.compose([
            this.validatorFactory.number({precision: Number.MAX_VALUE, allowNegative: false}),
          ]),
          descriptionEditable: true,
        };
        obj.propertyDescription = this.getChannelDescription(obj);
        obj.saveAction = (value: string) => {
          ArticleUsageUtils.setDefinedMetaElementPlausiPropertyFloatValue(
            this.articleUsage,
            channel.name,
            inputTargetMetaPropertyName,
            this.localeResourceManager.parseNumber(value)
          );
          ArticleUsageUtils.setDefinedMetaElementPlausiPropertyDescription(
            this.articleUsage,
            channel.name,
            inputTargetMetaPropertyName,
            obj.propertyDescription
          );
        };
        dataProvider.push(obj);
      }
      dataProvider.sort((a: any, b: any) => StringUtils.stringCompare(a.propertyLabel, b.propertyLabel));
    } else if (isDetailedEntryForCurrentConsumption === 0 && ioChannel.length > 0) {
      let value: number = PropertyUtils.getIntPropertyValue(this.articleUsage.plausiProperties, inputTargetChannelCountArticlePropertyName);
      let obj: any = {
        defaultValue: NumberUtils.isNotNumber(value) ? '' : this.localeResourceManager.formatNumber(value, false),
        propertyLabel: this.localeResourceManager.getValueByKey('settingsTabNavigator.label.used_channels'),
        inputRestricter: '^[0-9]*$',
        validator: Validators.compose([
          this.validatorFactory.number({precision: 0, allowNegative: false}),
        ]),
        saveAction: (value: string) =>
          PropertyUtils.setIntPropertyValue(
            this.articleUsage.plausiProperties,
            inputTargetChannelCountArticlePropertyName,
            this.localeResourceManager.parseNumber(value)
          ),
      };
      dataProvider.push(obj);

      value = PropertyUtils.getFloatPropertyValue(this.articleUsage.plausiProperties, inputTargetTotalArticlePropertyName);
      obj = {
        defaultValue: NumberUtils.isNotNumber(value) ? '' : this.localeResourceManager.formatNumber(value, false),
        propertyLabel: this.localeResourceManager.getValueByKey('settingsTabNavigator.label.sum'),
        inputRestricter: '^[0-9.,]*$',
        validator: Validators.compose([
          this.validatorFactory.number({precision: Number.MAX_VALUE, allowNegative: false}),
        ]),
        saveAction: (value: string) =>
          PropertyUtils.setFloatPropertyValue(
            this.articleUsage.plausiProperties,
            inputTargetTotalArticlePropertyName,
            this.localeResourceManager.parseNumber(value)
          ),
      };
      dataProvider.push(obj);
    }
    return dataProvider;
  }

  private getIOChannel(article: ArticleVO, metaElementSelectionPlausiPropertyName: string): any[] {
    const ioChannel: any[] = [];
    const metaElements: MetaElementVO[] = ArticleUtils.getConnectionAreasByValidBooleanPlausiPropertyName(
      article,
      metaElementSelectionPlausiPropertyName
    );
    for (const metaElement of metaElements) {
      const obj: any = {
        name: metaElement.name,
        label: PropertyUtils.getStringPropertyValue(metaElement.behaviorProperties, MetaElementBehaviorPropertyDef.DISPLAY_NAME),
      };
      ioChannel.push(obj);
    }

    const values: PropertyValueVO[] = PropertyUtils.getPropertyValuesByPropertyName(article.plausiProperties, metaElementSelectionPlausiPropertyName);
    for (const value of values) {
      const obj: any = {
        name: value.stringValue,
        label: value.stringValue,
      };
      ioChannel.push(obj);
    }
    return ioChannel;
  }

  private getChannelDescription(item: any): string {
    const description: string = ArticleUsageUtils.getDefinedMetaElementPlausiPropertyDescription(
      this.articleUsage,
      item.metaElementName,
      item.propertyName
    );
    if (!StringUtils.isEmpty(description)) {
      return description;
    } else {
      return '';
    }
  }

  public editDescription(item: any): void {
    this.projectComponentPresenter.openEditDescriptionComponent(item.propertyDescription).then((result) => {
      if (result.success) {
        item.propertyDescription = result.data;
      }
    });
  }
}
